import { useEffect, useRef, useState } from 'react'

import { ApiError } from '@/apis'
import { reportError } from '@/components/GlobalErrorBoundary/reportError'
import { useToast } from '@/components/Toast'
import { CTAButton } from '@/components/ui/CTAButton'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { NAVER_PAY_ONE_TIME_CHAIN_ID, NAVER_PAY_ONE_TIME_CLIENT_ID, NAVER_PAY_ONE_TIME_MODE } from '@/constants'
import { getPaymentFailureMessage, getUserFacingMessage } from '@/lib/errorMessage'
import { addPaymentBreadcrumb, setPaymentContext } from '@/lib/sentry'
import { BlockStockAlertModal, PaymentSummaryCard } from '@/pages/SealedVinylPage/components'
import type { BlockDialogKind } from '@/pages/SealedVinylPage/hooks/useProceedToPayment'
import { useProceedToPayment } from '@/pages/SealedVinylPage/hooks/useProceedToPayment'
import { PAYMENT_CONTEXT_STORAGE, SEALED_VINYL_DEFAULT_QUANTITY } from '@/pages/SealedVinylPage/lib/constants'
import { saveResume } from '@/pages/SealedVinylPage/lib/resume'
import { calcSealedVinylAmounts } from '@/pages/SealedVinylPage/lib/utils'
import { PAYMENT_METHOD_TYPES, usePostKakaopayWebviewRegister, usePostSealedOrder } from '@/payment'
import { openNaverpay } from '@/payment/sdk'
import { ROUTES } from '@/router/routes'

import type { PaymentMethod, RegisteredCard } from './components'
import { PaymentMethodCard } from './components'
import { PaymentMethodSelectSheet } from './components/PaymentMethodSelectSheet'
import { PaymentBottomBar, PaymentContainer, PaymentScrollArea, ProgressBarSection } from './styles'

interface PaymentStepProps {
  /** order step 에서 선택한 신청 수량(1~3). POST /product-orders 에 그대로 전달. 미지정 시 1. */
  quantity?: number
  /** 등록된 결제 카드 목록 — 부모(SealedVinylPage)에서 GET /payments/methods 결과 전달 */
  cards?: RegisteredCard[]
  /** NICEPAY 카드 등록(CardRegisterPage) 복귀 시 자동선택할 카드 id (resume). */
  initialSelectedCardId?: string
  /** "결제 카드 추가" placeholder 클릭 시 호출 — CardRegisterPage로 라우팅. 미지정 시 내부 BottomSheet 사용 */
  onAddCard?: () => void
  /** 결제 완료 시 호출 — funnel 다음 step(완료 화면) 이동 */
  onPaid: () => void
  /** 카드 직접 등록 클릭 시 호출 — CardRegisterPage 라우팅 */
  onNavigateToCardRegister?: () => void
}

export function PaymentStep({
  quantity = SEALED_VINYL_DEFAULT_QUANTITY,
  cards = [],
  initialSelectedCardId,
  onAddCard,
  onPaid,
  onNavigateToCardRegister,
}: PaymentStepProps) {
  const [method, setMethod] = useState<PaymentMethod>('card')
  /**
   * 캐러셀에서 보고 있는 슬라이드 — 카드면 id, "결제 카드 추가" 슬라이드면 null, 아직 미보고면 undefined.
   * CardCarousel 이 스와이프(스크롤 위치)에 따라 갱신. NICEPAY 등록 복귀 시엔 방금 등록한 카드로 시작.
   */
  const [viewedCardId, setViewedCardId] = useState<string | null | undefined>(initialSelectedCardId)
  // 직전 렌더의 카드 id 집합 — 카카오 등록 후 visibilitychange refetch 로 새 카드가 들어오면 자동선택
  const knownCardIdsRef = useRef(new Set(cards.map((card) => card.id)))
  /**
   * 결제 대상 카드 id. 미보고(undefined)면 첫 카드 기본 선택, add 슬라이드(null)면 null(결제 불가).
   * 목록이 바뀌어 stale 해진 id 는 첫 카드로 fallback.
   */
  const selectedCardId = (() => {
    if (viewedCardId === undefined) return cards[0]?.id ?? null
    if (viewedCardId === null) return null
    return cards.some((card) => card.id === viewedCardId) ? viewedCardId : (cards[0]?.id ?? null)
  })()
  const [isPaying, setIsPaying] = useState(false)
  const [showAddSheet, setShowAddSheet] = useState(false)
  const [blockDialog, setBlockDialog] = useState<BlockDialogKind | null>(null)
  const { show: showToast } = useToast()
  const { proceed } = useProceedToPayment()
  const postOrderMutation = usePostSealedOrder()
  const kakaopayRegisterMutation = usePostKakaopayWebviewRegister()

  // 카카오 카드 등록은 외부 앱 스킴으로 진행돼 webview 가 mount 유지된다. 복귀 시 visibilitychange 가
  // PAYMENT_METHODS 를 refetch → 직전에 없던 새 카드가 들어오면 그 카드를 자동선택한다.
  const cardIdsKey = cards.map((card) => card.id).join('|')
  useEffect(() => {
    const known = knownCardIdsRef.current
    const currentIds = cardIdsKey ? cardIdsKey.split('|') : []
    const newId = currentIds.find((id) => !known.has(id))
    knownCardIdsRef.current = new Set(currentIds)
    if (newId && known.size > 0) setViewedCardId(newId)
  }, [cardIdsKey])

  const handleSelectCardRegister = () => {
    setShowAddSheet(false)
    // CardRegisterPage 등록 후 navigate(-1) 로 복귀했을 때 PaymentStep + 선택 수량 자동 복원
    saveResume({ step: 'payment', quantity })
    if (onNavigateToCardRegister) {
      onNavigateToCardRegister()
    } else if (onAddCard) {
      onAddCard()
    }
  }

  const handleSelectKakaopayRegister = async () => {
    try {
      const response = await kakaopayRegisterMutation.mutateAsync()
      // 앱 스킴(android/iosAppScheme)으로 카톡 앱을 열면 동의 후 카카오가 다음 등록 플로우로
      // 진행하지 않아(승인 콜백 미연결) 사용할 수 없음 → 모바일 웹 URL 을 InAppWebView 안에서
      // 직접 열어 카카오 페이지가 동의·복귀를 오케스트레이션하게 한다.
      if (!response.nextRedirectMobileUrl) {
        throw new Error('카카오페이 redirect URL 이 응답에 없어요')
      }
      // full-navigation 으로 webview 가 unmount 되므로, 등록 successURL 복귀(카드 등록 콜백
      // 라우트) 시 PaymentStep·수량을 복원하도록 미리 sessionStorage 에 저장한다 (NICEPAY 와 동일).
      saveResume({ step: 'payment', quantity })
      setShowAddSheet(false)
      window.location.href = response.nextRedirectMobileUrl
    } catch (error) {
      showToast(getUserFacingMessage(error))
    }
  }

  // 단가 0원(무료 나눔) + 배송비 4500원 고정 → 총액은 수량과 무관하게 항상 4500원.
  const { itemAmount, deliveryFee, totalAmount } = calcSealedVinylAmounts(quantity)

  const handlePay = async () => {
    if (isPaying) return
    setIsPaying(true)

    setPaymentContext({ method, step: 'payment' })
    addPaymentBreadcrumb('결제 시작', { method, quantity })

    // 결제 직전 재고/가용성 재검증 — order step 진입 후 소진/부족됐을 수 있어 POST 전에 한 번 더 본다.
    const check = await proceed(quantity)
    if (!check.ok) {
      setIsPaying(false)
      if (check.kind === 'soldOut') {
        setBlockDialog('soldOut')
        return
      }
      if (check.kind === 'notEnoughStock') {
        setBlockDialog('notEnoughStock')
        return
      }
      showToast(check.message)
      return
    }

    try {
      const orderResponse = await dispatchSealedOrder()
      if (!orderResponse) {
        setIsPaying(false)
        return
      }

      const { paymentId } = orderResponse
      setPaymentContext({ method, step: 'payment', paymentId })
      addPaymentBreadcrumb('주문 생성', { method, paymentId })

      try {
        sessionStorage.setItem(PAYMENT_CONTEXT_STORAGE.PAYMENT_ID, String(paymentId))
        sessionStorage.setItem(PAYMENT_CONTEXT_STORAGE.AMOUNT, String(totalAmount))
      } catch {
        // iOS Safari Private 모드 등 — ProcessPage 가 missingContext 로 처리.
      }

      if (method === 'card') {
        addPaymentBreadcrumb('PG 진입: card (즉시 완료)', { paymentId })
        setIsPaying(false)
        onPaid()
        return
      }

      if (method === 'kakaopay') {
        if (!orderResponse.nextRedirectMobileUrl) {
          throw new Error('카카오페이 redirect URL 이 응답에 없어요')
        }
        addPaymentBreadcrumb('PG 진입: kakaopay (redirect)', { paymentId })
        window.location.href = orderResponse.nextRedirectMobileUrl
        return
      }

      if (method === 'naverpay') {
        // 네이버페이 취소/실패로 process 페이지가 /sealed-vinyl 로 복귀할 때
        // PaymentStep 이 복원되도록 미리 resume 저장 (카카오 등록 흐름과 동일 패턴)
        saveResume({ step: 'payment', quantity })
        addPaymentBreadcrumb('PG 진입: naverpay (SDK)', { paymentId })
        const returnUrl = `${window.location.origin}${ROUTES.SEALED_VINYL_PROCESS}`
        await openNaverpay(NAVER_PAY_ONE_TIME_CLIENT_ID, NAVER_PAY_ONE_TIME_CHAIN_ID, NAVER_PAY_ONE_TIME_MODE, {
          amount: totalAmount,
          orderId: String(paymentId),
          productName: '밀봉비닐',
          productUid: 'SEALED_VINYL',
          productCount: quantity,
          merchantUserKey: `onl_${paymentId}`,
          returnUrl,
        })
        return
      }
    } catch (error) {
      // PG 거절(잔액부족·한도초과 등)은 envelope.message 가 "결제에 실패했습니다" 일반 문구라
      // 실제 사유가 담긴 error.description 을 우선 노출한다.
      showToast(getPaymentFailureMessage(error))
      // 거절(ApiError)은 사유 code 별로 그룹핑해 Sentry warning 으로 집계 — 빈도·추세 추적용(알림 소음 X).
      if (error instanceof ApiError) {
        reportError(error, { source: 'payment-decline', level: 'warning', fingerprint: ['payment-decline', error.code] })
      }
      setIsPaying(false)
    }
  }

  const dispatchSealedOrder = async () => {
    if (method === 'card') {
      if (selectedCardId == null) {
        showToast('카드를 선택해주세요')
        setIsPaying(false)
        return null
      }
      return postOrderMutation.mutateAsync({
        quantity,
        method: { id: Number(selectedCardId), type: PAYMENT_METHOD_TYPES.CARD },
      })
    }

    if (method === 'kakaopay') {
      return postOrderMutation.mutateAsync({
        quantity,
        method: { id: null, type: PAYMENT_METHOD_TYPES.KAKAOPAY },
      })
    }

    if (method === 'naverpay') {
      return postOrderMutation.mutateAsync({
        quantity,
        method: { id: null, type: PAYMENT_METHOD_TYPES.NAVERPAY },
      })
    }

    return null
  }

  return (
    <PaymentContainer>
      <ProgressBarSection>
        <ProgressBar
          current={2}
          total={2}
        />
      </ProgressBarSection>
      <PaymentScrollArea>
        <PaymentSummaryCard
          itemAmount={itemAmount}
          deliveryFee={deliveryFee}
          totalAmount={totalAmount}
        />
        <PaymentMethodCard
          selected={method}
          onSelect={setMethod}
          cards={cards}
          onAddCard={() => setShowAddSheet(true)}
          selectedCardId={selectedCardId}
          onSelectCard={setViewedCardId}
        />
      </PaymentScrollArea>
      <PaymentBottomBar>
        <CTAButton
          variant='brand'
          loading={isPaying}
          disabled={method === 'card' && selectedCardId == null}
          onClick={handlePay}
        >
          결제하기
        </CTAButton>
      </PaymentBottomBar>
      {showAddSheet && (
        <PaymentMethodSelectSheet
          open={showAddSheet}
          onClose={() => setShowAddSheet(false)}
          onSelectCard={handleSelectCardRegister}
          onSelectKakaopay={handleSelectKakaopayRegister}
          isPending={kakaopayRegisterMutation.isPending}
        />
      )}
      {blockDialog && (
        <BlockStockAlertModal
          kind={blockDialog}
          onClose={() => setBlockDialog(null)}
        />
      )}
    </PaymentContainer>
  )
}
